import { useState, useEffect } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ProcessTracker from '@/components/ProcessTracker';
import EEOCOverdueAlert from '@/components/EEOCOverdueAlert';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LayoutDashboard,
  Phone,
  AlertTriangle,
  Users,
  FileText,
  BookOpen,
  Award,
  Settings,
  LogOut,
  ChevronDown,
  Building2,
  Crown,
  Menu,
  X,
  CreditCard,
  UserCircle,
  UserSearch,
  FileBarChart,
  GraduationCap,
  ClipboardCheck,
  Trophy,
  FolderOpen,
  Activity,
  Cloud,
  Target,
  MessageCircle,
  HelpCircle,
  Sun,
  Moon,
  ChevronRight,
  Network,
  Trash2,
  Brain,
  Lock,
  Shield,
  ClipboardList,
  BarChart3,
  FileSearch,
  RefreshCw,
  Send,
  Database,
  Play,
  Bot
} from 'lucide-react';
import { cn } from '@/lib/utils';
import NotificationBell from '@/components/NotificationBell';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/ThemeToggle';

// Feature restriction mapping
// These IDs map to the restricted_features array from the role
const FEATURE_RESTRICTIONS = {
  'master-delete': 'master_delete',
  'billing': 'billing',
  'onedrive': 'azure_config', // OneDrive page has Azure config section
};

// Full navigation matching AdminLayout
// Note: user-session, role-management, location-management, and audio-settings are hidden
// - user-session: accessible via clicking user name in Users table
// - role-management, location-management, audio-settings: consolidated into Settings page tabs
const fullNavigation = [
  { id: 'dashboard', name: 'Analytics Dashboard', href: '', icon: BarChart3, section: 'main' },
  { id: 'users', name: 'Users', href: 'users', icon: Users, section: 'main' },
  // { id: 'user-session', name: 'User Session', href: 'user-session', icon: UserCircle, section: 'main' }, // Hidden - access via Users table
  { id: 'calls', name: 'Calls', href: 'calls', icon: Phone, section: 'main' },
  { id: 'search-applicants', name: 'Search Applicants', href: 'search-applicants', icon: UserSearch, section: 'main' },
  { id: 'team-reports', name: 'Team Reports', href: 'team-reports', icon: Network, section: 'main' },
  { id: 'alerts', name: 'Alerts', href: 'alerts', icon: AlertTriangle, section: 'analytics' },
  { id: 'auditors-dashboard', name: 'Auditors Dashboard', href: 'auditors-dashboard', icon: Shield, section: 'analytics' },
  { id: 'reports', name: 'Notes Compliance', href: 'reports', icon: FileBarChart, section: 'analytics' },
  { id: 'kpi', name: 'KPI', href: 'kpi', icon: Target, section: 'analytics' },
  { id: 'qa-notes', name: 'QA Notes', href: 'qa-notes', icon: ClipboardList, section: 'analytics' },
  { id: 'case-studies', name: 'Case Studies', href: 'case-studies', icon: FileSearch, section: 'analytics' },
  { id: 'review-requests', name: 'Review Requests', href: 'review-requests', icon: Send, section: 'analytics' },
  { id: 'submissions', name: 'Submissions', href: 'submissions', icon: Database, section: 'analytics' },
  { id: 'reprocess-calls', name: 'Re-Process Calls', href: 'reprocess-calls', icon: RefreshCw, section: 'system' },
  // { id: 'role-management', name: 'Role Management', href: 'role-management', icon: Shield, section: 'system' }, // Moved to Settings
  // { id: 'location-management', name: 'Location Management', href: 'location-management', icon: Building2, section: 'system' }, // Moved to Settings
  { id: 'ai-coach', name: 'AI Communication Coach', href: 'ai-coach', icon: MessageCircle, section: 'tools', highlight: true },
  { id: 'mock-training', name: 'Mock Training', href: 'mock-training', icon: Play, section: 'tools' },
  { id: 'library', name: 'Library', href: 'library', icon: FolderOpen, section: 'tools' },
  { id: 'leaderboard', name: 'Leaderboard', href: 'leaderboard', icon: Trophy, section: 'tools' },
  { id: 'eeoc', name: 'EEOC Training', href: 'eeoc', icon: Shield, section: 'tools', featureFlag: 'eeoc_enabled' },
  { id: 'repository', name: 'Repository', href: 'repository', icon: FolderOpen, section: 'resources' },
  { id: 'scoring-guide', name: 'Scoring Guide', href: 'scoring-guide', icon: BookOpen, section: 'resources' },
  { id: 'team', name: 'Know the Team', href: 'team', icon: Users, section: 'resources' },
  { id: 'support', name: 'Support Center', href: 'support', icon: MessageCircle, section: 'help' },
  { id: 'audit-trail', name: 'Audit Trail', href: 'audit-trail', icon: Shield, section: 'system' },
  { id: 'process-config', name: 'Process Config', href: 'process-config', icon: Settings, section: 'system' },
  { id: 'documentation', name: 'App Documentation', href: 'documentation', icon: FileText, section: 'help' },
  { id: 'billing', name: 'Billing', href: 'billing', icon: CreditCard, section: 'system', restrictedFeature: 'billing' },
  // { id: 'audio-settings', name: 'Audio & Call Settings', href: 'audio-settings', icon: Settings, section: 'system', restrictedFeature: 'audio_settings' }, // Moved to Settings
  { id: 'settings', name: 'Settings', href: 'settings', icon: Settings, section: 'system' },
  { id: 'onedrive', name: 'OneDrive', href: 'onedrive', icon: Cloud, section: 'system', restrictedFeature: 'onedrive' },
  { id: 'onedrive-inventory', name: 'OneDrive Inventory', href: 'onedrive-inventory', icon: Database, section: 'system' },
  { id: 'pipeline-bot', name: 'Pipeline Bot', href: 'pipeline-bot', icon: Bot, section: 'system' },
  { id: 'fine-tune-llm', name: 'Fine-Tune LLM', href: 'fine-tune-llm', icon: Brain, section: 'system' },
  { id: 'master-delete', name: 'Master Delete', href: 'master-delete', icon: Trash2, section: 'system', restrictedFeature: 'master_delete' },
];

const navSections = [
  { id: 'main', label: 'Main' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'tools', label: 'AI Tools' },
  { id: 'resources', label: 'Resources' },
  { id: 'help', label: 'Help' },
  { id: 'system', label: 'System' },
];

export default function TenantLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, logout, isAuthenticated, loading, isSuperAdmin, restrictedFeatures, isFeatureRestricted } = useAuth();
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [users, setUsers] = useState([]);

  // Check for impersonation mode
  const searchParams = new URLSearchParams(location.search);
  const impersonateToken = searchParams.get('impersonate');
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Audit tracking for page views
  useEffect(() => {
    if (isAuthenticated && user) {
      // Track page view silently
      import('@/lib/api').then(({ trackUserAction }) => {
        trackUserAction('page_view', location.pathname).catch(() => {});
      });
    }
  }, [location.pathname, isAuthenticated, user]);

  // Check if a nav item is restricted for current user
  const isNavItemRestricted = (item) => {
    if (!item.restrictedFeature) return false;
    // Use the auth context's isFeatureRestricted which checks user's feature_access
    return isFeatureRestricted(item.restrictedFeature);
  };

  // Check if a nav item is hidden by feature flag (tenant-level feature toggle)
  const isNavItemHidden = (item) => {
    if (!item.featureFlag) return false;
    // Check if tenant has the feature enabled
    const features = tenant?.features || {};
    return !features[item.featureFlag];
  };

  // Fetch users for UserSession page
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get('/tenant/users');
        setUsers(data.users || data || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    if (isAuthenticated && !isSuperAdmin) {
      fetchUsers();
    }
  }, [isAuthenticated, isSuperAdmin]);

  useEffect(() => {
    // Handle impersonation token from URL
    if (impersonateToken) {
      localStorage.setItem('saas_token', impersonateToken);
      setIsImpersonating(true);
      // Remove token from URL
      window.history.replaceState({}, '', location.pathname);
      window.location.reload();
    }
  }, [impersonateToken]);

  useEffect(() => {
    if (!loading && !isAuthenticated && !impersonateToken) {
      // Use replace to avoid polluting browser history with auth redirects
      navigate('/login', { replace: true });
    }
    if (!loading && isSuperAdmin && !impersonateToken) {
      navigate('/super-admin/dashboard', { replace: true });
    }
  }, [loading, isAuthenticated, isSuperAdmin, navigate, impersonateToken]);

  // Check if current token is impersonation
  useEffect(() => {
    const token = localStorage.getItem('saas_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.impersonation_mode) {
          setIsImpersonating(true);
        }
      } catch (e) {}
    }
  }, []);

  if (loading) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center",
        theme === 'dark' ? 'bg-slate-900' : 'bg-gray-100'
      )}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!user || !tenant) {
    // Show loading state while authenticating, not blank page
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center",
        theme === 'dark' ? 'bg-slate-900' : 'bg-gray-100'
      )}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className={cn("text-sm", theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500')}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  const currentPath = location.pathname.replace('/dashboard/', '').replace('/dashboard', '') || '';
  
  // Apply custom branding
  const branding = tenant.branding || {};
  const primaryColor = branding.primary_color || branding.colors?.primary || '#3b82f6';
  const whiteLabel = branding.white_label || branding.whitelabel_enabled || false;

  const bgClass = theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50';
  const sidebarBgClass = theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-card border-gray-200';
  const headerBgClass = theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-card border-gray-200';
  const textClass = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedTextClass = theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500';
  const hoverBgClass = theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-100';

  return (
    <div className={cn("min-h-screen flex", bgClass)}>
      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col border-r transition-all duration-300",
        sidebarBgClass,
        sidebarOpen ? "w-64" : "w-20"
      )}>
        {/* Logo */}
        <div className={cn("flex items-center h-16 px-4 border-b", theme === 'dark' ? 'border-slate-700' : 'border-gray-200')}>
          <Link to="/dashboard" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={tenant.name} className="h-8 w-8 rounded-lg object-contain" />
            ) : (
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
                <Building2 className="h-5 w-5" style={{ color: primaryColor }} />
              </div>
            )}
            {sidebarOpen && (
              <div>
                <h1 className={cn("text-lg font-semibold", textClass)}>
                  {tenant.branding?.company_name || tenant.name}
                </h1>
                {!whiteLabel && <p className={cn("text-xs", mutedTextClass)}>Insyra AI</p>}
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {navSections.map((section) => {
            const sectionItems = fullNavigation.filter(item => item.section === section.id && !isNavItemHidden(item));
            if (sectionItems.length === 0) return null;
            
            return (
              <div key={section.id} className="mb-6">
                {sidebarOpen && (
                  <h3 className={cn("px-3 mb-2 text-xs font-semibold uppercase tracking-wider", mutedTextClass)}>
                    {section.label}
                  </h3>
                )}
                <div className="space-y-1">
                  {sectionItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPath === item.href || (item.href === '' && currentPath === '');
                    const isRestricted = isNavItemRestricted(item);
                    
                    // Render restricted items as non-clickable with lock icon
                    if (isRestricted) {
                      return (
                        <TooltipProvider key={item.id}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium cursor-not-allowed opacity-50",
                                  mutedTextClass
                                )}
                                title={!sidebarOpen ? `${item.name} (Restricted)` : undefined}
                              >
                                <Icon className="h-5 w-5 flex-shrink-0" />
                                {sidebarOpen && (
                                  <span className="flex items-center gap-2">
                                    {item.name}
                                    <Lock className="h-3 w-3" />
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>Access restricted for your role</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }
                    
                    return (
                      <Link
                        key={item.id}
                        to={`/dashboard/${item.href}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                          isActive
                            ? "bg-blue-500/20 text-blue-500"
                            : cn(mutedTextClass, hoverBgClass, "hover:text-white")
                        )}
                        title={!sidebarOpen ? item.name : undefined}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        {sidebarOpen && <span>{item.name}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Sidebar Toggle */}
        <div className={cn("p-3 border-t", theme === 'dark' ? 'border-slate-700' : 'border-gray-200')}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={cn("w-full justify-center", mutedTextClass)}
          >
            <ChevronRight className={cn("h-4 w-4 transition-transform", !sidebarOpen && "rotate-180")} />
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn("flex-1 flex flex-col", sidebarOpen ? "ml-64" : "ml-20")}>
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-purple-600 text-white text-center py-2 px-4 text-sm">
            <span className="font-medium">Super Admin Mode:</span> You are viewing {tenant.name}'s dashboard. 
            <button 
              onClick={() => { localStorage.removeItem('saas_token'); window.location.href = '/super-admin/dashboard'; }}
              className="ml-2 underline hover:no-underline"
            >
              Exit
            </button>
          </div>
        )}

        {/* Header */}
        <header className={cn("sticky top-0 z-40 border-b", headerBgClass)}>
          <div className="flex items-center justify-between h-16 px-6">
            {/* Left - Breadcrumb or Search */}
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="border-blue-500/30 text-blue-500">
                {tenant.subscription_status === 'trialing' ? (
                  <>Trial</>  
                ) : (
                  <><Crown className="h-3 w-3 mr-1" />{tenant.subscription_status}</>
                )}
              </Badge>
            </div>

            {/* Right - Actions */}
            <div className="flex items-center gap-4">
              {/* Workspace Switcher — multi-tenant access (Feb 2026) */}
              <WorkspaceSwitcher />

              {/* Theme Toggle */}
              <ThemeToggle variant="icon" />

              {/* Notifications Bell */}
              <NotificationBell 
                userId={user?.id || user?.user_id}
                userRole={user?.role || 'auditor'}
                tenantId={user?.tenant_id}
              />

              {/* Help */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard/documentation')}
                className={mutedTextClass}
              >
                <HelpCircle className="h-5 w-5" />
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={cn("flex items-center gap-2", mutedTextClass)}>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatar_url} />
                      <AvatarFallback className="bg-blue-500/20 text-blue-500">
                        {user.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block">{user.name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={cn("w-56", theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-card border-gray-200')}>
                  <DropdownMenuLabel className={mutedTextClass}>
                    <div className="flex flex-col">
                      <span className={textClass}>{user.name}</span>
                      <span className="text-xs font-normal">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className={theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'} />
                  <DropdownMenuItem
                    onClick={() => navigate('/dashboard/settings')}
                    className={cn("cursor-pointer", mutedTextClass, theme === 'dark' ? 'focus:bg-slate-700' : 'focus:bg-gray-100')}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/dashboard/documentation')}
                    className={cn("cursor-pointer", mutedTextClass, theme === 'dark' ? 'focus:bg-slate-700' : 'focus:bg-gray-100')}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Documentation
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'} />
                  <DropdownMenuItem
                    onClick={logout}
                    className={cn("text-red-500 cursor-pointer", theme === 'dark' ? 'focus:bg-slate-700' : 'focus:bg-gray-100')}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          {/* EEOC Training Overdue Alert for Admins */}
          {(user?.role === 'admin' || user?.role === 'owner' || user?.is_admin) && (
            <EEOCOverdueAlert className="mb-6" />
          )}
          
          <Outlet context={{ tenant, user, users, isSaasTenant: true, theme }} />
        </main>
        
        {/* Process Tracker - Floating panel for background processes */}
        <ProcessTracker />
      </div>
    </div>
  );
}
